import { Icon } from '@design/components/icon/Icon';
import { TableCell } from '@design/components/table';
import { useDragOperation } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { Row } from '@tanstack/react-table';
import { DRAG_TYPES, RowDragData } from '@terminal/constants/drag-types';
import clsx from 'clsx';
import { HTMLAttributes, ReactNode } from 'react';
import styles from './DraggableWatchlistRow.module.scss';
import { isRowDrag } from './dragUtils';

export interface DraggableWatchlistRowProps<T>
  extends Omit<HTMLAttributes<HTMLTableRowElement>, 'onDrop'> {
  children: ReactNode;
  row: Row<T>;
  afterNode?: ReactNode;
  onDrop?: ({ toIdx, fromIdx }: { toIdx: number; fromIdx: number }) => void;
}

export const DraggableWatchlistRow = <T,>({
  children,
  row,
  afterNode,
  onDrop,
  ...rest
}: DraggableWatchlistRowProps<T>) => {
  const idx = row.index;

  const sortable = useSortable({
    id: row.id,
    index: idx,
    type: DRAG_TYPES.TABLE_ROW,
    accept: DRAG_TYPES.TABLE_ROW,
    disabled: !onDrop,
    data: { idx } satisfies RowDragData,
  });

  const { source } = useDragOperation();
  const sourceIdx = isRowDrag(source?.data) ? source.data.idx : undefined;
  const canDrop =
    sortable.isDropTarget && sourceIdx !== undefined && sourceIdx !== idx;
  const dropPosition =
    sourceIdx !== undefined && idx > sourceIdx ? 'bottom' : 'top';

  return (
    <tr
      ref={sortable.ref}
      className={clsx(styles.styledDraggableRow, {
        [styles.isTopOver]: canDrop && dropPosition === 'top',
        [styles.isBottomOver]: canDrop && dropPosition === 'bottom',
      })}
      {...rest}
    >
      <TableCell
        ref={sortable.handleRef}
        className={styles.styledTableCell}
        data-dwr-table-cell
      >
        <div
          className={styles.cellContentWrapper}
          data-dwr-cell-content-wrapper
        >
          {onDrop && (
            <div>
              <Icon
                className={styles.dragIcon}
                size={12}
                iconName="GripsDots"
              />
            </div>
          )}
          {afterNode}
        </div>
      </TableCell>
      {children}
    </tr>
  );
};
